/**
 * React Query hooks for the unified inbox (plan Phase 9).
 *
 * Separate from `useInbox.js` on purpose. That file is the PERSONAL inbox: Gmail
 * and Outlook per-user OAuth, CRM communications, the tracking table. This one
 * reads `/api/inboxes/*`, where every request is authorised per inbox and every
 * read writes an audit row. Sharing a module would put two different
 * authorisation models behind one import and make it easy to reach for the wrong
 * hook.
 *
 * Query keys are per-inbox (`['inbox', inboxId, ...]`) rather than global. With a
 * global key, switching mailboxes would show the previous inbox's messages until
 * the refetch landed — someone else's mail, briefly, in a feature whose entire
 * point is that access is scoped.
 */
import { useCallback, useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '../context/AuthContext';
import { apiDelete, apiFetch, apiFetchArray, apiPatch, apiPost } from '../lib/api';
import { isOrgAdminUser } from '../utils/orgRoles';
// One key factory shared with the rail, so an invalidation here can never
// drift from the key the rail's counts are cached under (#710).
import { mailFolderKeys } from './useMailFolders';
import { notifyBellChanged } from '../lib/bellLinkage';
import { removeThreadsContaining } from './threadCaches';
import { withoutSignatureBlocks } from '../lib/editorSafeHtml';

export const inboxKeys = {
    inboxes: ['inboxes'],
    orphans: ['inboxes', 'orphans'],
    employees: ['admin', 'inbox', 'employees'],
    // `mailbox` rides in the key on purpose: the unified surface polls stats
    // FILTERED by the receiving-mailbox filter while AdminUnifiedInboxTab polls
    // the same inbox unfiltered. One shared key would let each poll overwrite
    // the other's answer every 60s — the header counts would flip between "the
    // list on screen" and "the whole inbox" depending on which tab ran last.
    // Bare `inboxKeys.stats(inboxId)` calls resolve to the unfiltered entry,
    // while `statsPrefix` invalidations refresh every mailbox-filtered variant
    // after a mutation.
    stats: (inboxId, mailbox = '') => ['inbox', inboxId, 'stats', mailbox],
    statsPrefix: inboxId => ['inbox', inboxId, 'stats'],
    members: (inboxId, search = '', pageSize = 20) => [
        'inbox',
        inboxId,
        'members',
        search,
        pageSize,
    ],

    messages: (inboxId, filters) => ['inbox', inboxId, 'messages', filters],
    thread: (inboxId, conversationId) => ['inbox', inboxId, 'thread', conversationId],
    unrepliedCounts: ['inboxes', 'unreplied'],
};

/**
 * Query keys for per-inbox mailbox connections (Gmail + Outlook).
 *
 * Separate from `inboxKeys` because mailboxes are a sub-resource that has its
 * own invalidation surface: attach/detach mutations invalidate only the
 * mailbox list for one inbox, not the inbox's messages or stats.
 */
export const mailboxKeys = {
    all: ['mailboxes'],
    connection: id => ['mailboxes', id],
    inboxRefs: inboxId => ['inbox', inboxId, 'mailboxes'],
};

/**
 * 404 is the feature flag, not a bug.
 *
 * Every `/api/inboxes/*` route answers 404 while `RUNTIME_UNIFIED_INBOX_ENABLED`
 * is off — deliberately, so a probe cannot enumerate which tenants have it on.
 * The UI therefore has to read a 404 as "this tenant does not have the feature"
 * and render nothing, not as an error worth a toast.
 */
function isFeatureOff(error) {
    return error?.response?.status === 404;
}

export function useOrphanedInboxes() {
    const { getAuthHeader, user } = useAuth();
    return useInfiniteQuery({
        queryKey: inboxKeys.orphans,
        enabled: isOrgAdminUser(user),
        initialPageParam: null,
        queryFn: ({ pageParam }) => {
            const params = new URLSearchParams({ limit: '50' });
            if (pageParam) params.set('cursor', pageParam);
            return apiFetch(`/inboxes/orphans?${params.toString()}`, getAuthHeader());
        },
        getNextPageParam: lastPage =>
            lastPage?.has_more ? lastPage.next_cursor : undefined,
        select: result => {
            const pages = result?.pages || [];
            const first = pages[0] || {};
            return {
                data: pages.flatMap(page => page?.data || []),
                total: first.total || 0,
                org_inbox_count: first.org_inbox_count || 0,
            };
        },
        retry: false,
        staleTime: 30 * 1000,
    });
}


export function useInboxes() {

    const { getAuthHeader, user } = useAuth();
    return useQuery({
        queryKey: inboxKeys.inboxes,
        queryFn: () => apiFetch('/inboxes', getAuthHeader()),
        enabled: Boolean(user),
        select: data => data?.data || [],
        // No retry: the expected failure is a 404 from the feature flag, and
        // retrying it three times per page load buys nothing.
        retry: false,
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    });
}

/**
 * Whether to show the unified inbox at all — the route guard and sidebar tile.
 *
 * Every caller, including admins, needs at least one grant. This is UX only:
 * `require_inbox_access` on the server is what actually enforces it, and it
 * re-reads grants on every request so a revoked grantee loses access on their
 * next call rather than when this cache expires.
 */
export function useInboxAccess() {
    const { user } = useAuth();
    const { data: inboxes = [], isLoading, error } = useInboxes();

    const isAdmin = isOrgAdminUser(user);
    const featureOff = isFeatureOff(error);

    return {
        isLoading,
        featureOff,
        inboxes,
        // Inbox access is grant-bound for admins too. Settings remains the
        // recovery/setup surface, so an admin with no grant must not receive a
        // second implicit path into the shared mailbox view.
        hasAccess: !featureOff && inboxes.length > 0,
        isAdmin,
    };
}

/**
 * Per-inbox unreplied counts for the MailboxSwitcher's awaiting-reply badges.
 *
 * Calls `GET /inboxes?include=unreplied` once and maps the response into
 * `{ [inbox_id]: count }`. The backend does a single aggregation across all
 * visible inboxes — no N+1 stats calls. Polls every 60s as a fallback; SSE
 * (when connected) invalidates this query on `inbox_message` events.
 */
export function useInboxUnrepliedCounts(inboxes = []) {
    const { getAuthHeader, user } = useAuth();
    return useQuery({
        queryKey: inboxKeys.unrepliedCounts,
        enabled: Boolean(user) && inboxes.length > 0,
        queryFn: () =>
            apiFetch('/inboxes?include=unreplied', getAuthHeader()),
        select: data => {
            const map = {};
            for (const row of data?.data || []) {
                if (row.inbox_id && typeof row.unreplied === 'number') {
                    map[row.inbox_id] = row.unreplied;
                }
            }
            return map;
        },
        retry: false,
        staleTime: 30 * 1000,
        refetchInterval: 60 * 1000,
    });
}

export function useInboxMessages(inboxId, filters = {}) {
    const { getAuthHeader } = useAuth();
    return useQuery({
        queryKey: inboxKeys.messages(inboxId, filters),
        enabled: Boolean(inboxId),
        queryFn: () => {
            const qs = new URLSearchParams();
            if (filters.sender) qs.set('sender', filters.sender);
            if (filters.recipient) qs.set('recipient', filters.recipient);
            // Repeated `mailboxes` params — FastAPI accepts the same name many
            // times and the backend lowercases/caps the list. The URL carries
            // one comma-joined value; here it fans back out.
            (filters.mailbox || '')
                .split(',')
                .map(s => s.trim())
                .filter(Boolean)
                .forEach(m => qs.append('mailboxes', m));
            if (filters.search) qs.set('search', filters.search);
            // The rail's view. `inbox` is the server's default, so it is left
            // off to keep the request readable; anything else must be sent or
            // the rail highlights a folder the list never asks for.
            if (filters.folder && filters.folder !== 'inbox') {
                qs.set('folder', filters.folder);
            }
            // #646 — the rail's tag filter (a tag_id), composed with the folder.
            if (filters.tag) qs.set('tag', filters.tag);
            if (filters.dateFrom) qs.set('date_from', filters.dateFrom);
            if (filters.dateTo) qs.set('date_to', filters.dateTo);
            if (filters.replied !== undefined && filters.replied !== null) {
                qs.set('replied', String(filters.replied));
            }
            // Sorting is the SERVER's job, and sending these is what makes it so.
            // While they were omitted the database always returned the newest
            // rows and the browser re-sorted whatever had been loaded — so
            // "sort by subject" meant "the alphabetically first of the most
            // recent 25", an answer that changed as you scrolled.
            if (filters.sort) qs.set('sort', filters.sort);
            if (filters.direction) qs.set('direction', filters.direction);
            // A cursor names a POSITION; `page` counts an offset. Sending both
            // would skip twice, so the cursor wins when there is one and the
            // server ignores `page` entirely.
            if (filters.cursor) qs.set('cursor', filters.cursor);
            qs.set('page', String(filters.page || 1));
            // Server caps at 200; asking for more is a 422, so the cap is applied
            // here too rather than discovered by the user.
            qs.set('page_size', String(Math.min(filters.pageSize || 25, 200)));
            return apiFetch(`/inboxes/${inboxId}/messages?${qs.toString()}`, getAuthHeader());
        },
        // Keeps the previous page visible while the next loads — paging a mailbox
        // that blanks out on every click reads as a broken table.
        placeholderData: previous => previous,
        retry: false,
        staleTime: 30 * 1000,
        refetchInterval: 60 * 1000,
    });
}

// Per-thread post-send polling deadlines. When a reply is sent, the deadline
// for that (inboxId, conversationId) is set to Date.now() + 30000.
// useInboxThread checks its own deadline to enable 3s refetchInterval during
// the 30-second window, so the thread picks up the webhook-driven ingest of
// the sent reply without requiring a manual page refresh.
//
// Per-thread scoping prevents a reply in one thread from triggering polling
// on other threads that were never replied to — important if multiple
// EmailDetailPage instances could mount simultaneously (e.g. split-pane).
const _postSendPollingUntil = new Map();

export function useInboxThread(inboxId, conversationId) {
    const { getAuthHeader } = useAuth();
    return useQuery({
        queryKey: inboxKeys.thread(inboxId, conversationId),
        enabled: Boolean(inboxId && conversationId),
        queryFn: () =>
            apiFetch(
                `/inboxes/${inboxId}/threads/${encodeURIComponent(conversationId)}`,
                getAuthHeader()
            ),
        retry: false,
        staleTime: 15 * 1000,
        // #803 — always re-check on open, so a thread deleted elsewhere since it
        // was cached answers 404 instead of rendering from the cache.
        refetchOnMount: 'always',
        // Poll every 3s during the post-send window, otherwise no polling.
        // Using a function form so the check re-evaluates on each tick rather
        // than once at hook construction — avoids a race where the reply
        // onSuccess fires after useInboxThread was already evaluated.
        refetchInterval: () => {
            const deadline = _postSendPollingUntil.get(`${inboxId}:${conversationId}`);
            return deadline && Date.now() < deadline ? 3000 : false;
        },
    });
}

/**
 * A conversation as it sits in this inbox's TRASH — the reader's read-only
 * Trash mode, opened from a Trash row (`?folder=trash`).
 *
 * `useInboxThread` cannot serve it: the live thread route seeds from the
 * inbox tag, which a trashed row no longer carries, so a fully trashed
 * conversation opened on a 404 and a partly trashed one (the reader's
 * per-message Delete) on its live remainder. This returns exactly the rows the
 * Trash verbs restore or purge.
 *
 * Keyed under `thread` so every invalidation that refreshes threads after a
 * delete, restore or purge refreshes this one too.
 */
export function useInboxTrashThread(inboxId, seed, { enabled = true } = {}) {
    const { getAuthHeader } = useAuth();
    return useQuery({
        queryKey: [...inboxKeys.thread(inboxId, seed), 'trash'],
        enabled: Boolean(enabled && inboxId && seed),
        queryFn: () =>
            apiFetch(
                `/inboxes/${inboxId}/trash/${encodeURIComponent(seed)}`,
                getAuthHeader()
            ),
        retry: false,
        staleTime: 15 * 1000,
        // #803 — re-check on open, like the live thread: a Trash conversation
        // restored or deleted for good elsewhere answers 404 instead of
        // rendering from the cache.
        refetchOnMount: 'always',
    });
}

/**
 * Header stats for one inbox, optionally scoped to a set of receiving
 * mailboxes.
 *
 * `mailbox` is the same comma-joined SMTP list the messages query sends, so
 * the header counts describe the list on screen rather than quietly
 * contradicting the filter. Omitted/empty means unfiltered, which is also the
 * key AdminUnifiedInboxTab reads — see the note on `inboxKeys.stats`.
 */
export function useInboxStats(inboxId, { mailbox } = {}) {
    const { getAuthHeader } = useAuth();
    return useQuery({
        queryKey: inboxKeys.stats(inboxId, mailbox || ''),
        enabled: Boolean(inboxId),
        queryFn: () => {
            const qs = new URLSearchParams();
            (mailbox || '')
                .split(',')
                .map(s => s.trim())
                .filter(Boolean)
                .forEach(m => qs.append('mailboxes', m));
            const suffix = qs.toString() ? `?${qs.toString()}` : '';
            return apiFetch(`/inboxes/${inboxId}/stats${suffix}`, getAuthHeader());
        },
        retry: false,
        staleTime: 30 * 1000,
        refetchInterval: 60 * 1000,
    });
}

/**
 * Infinite, cacheable member (receiving-mailbox) rows for one inbox.
 *
 * This is the single owner of the members endpoint. The mailbox filter dropdown
 * supplies its debounced search term and calls `fetchNextPage`; it does not
 * construct URLs, manage auth, append rows, or race-cancel requests itself.
 * React Query keys each search independently and deduplicates cached pages,
 * while `getNextPageParam` keeps the server's total/page contract authoritative.
 */
export function useInboxMembers(
    inboxId,
    { search = '', pageSize = 20, enabled = true } = {},
) {
    const { getAuthHeader } = useAuth();
    const normalizedSearch = String(search || '').trim();
    const normalizedPageSize = Math.min(Math.max(Number(pageSize) || 20, 1), 100);

    return useInfiniteQuery({
        queryKey: inboxKeys.members(inboxId, normalizedSearch, normalizedPageSize),
        enabled: Boolean(inboxId) && enabled,
        initialPageParam: 1,
        queryFn: ({ pageParam = 1 }) => {
            const qs = new URLSearchParams();
            if (normalizedSearch) qs.set('search', normalizedSearch);
            qs.set('page', String(pageParam));
            qs.set('page_size', String(normalizedPageSize));
            return apiFetch(
                `/inboxes/${encodeURIComponent(inboxId)}/members?${qs.toString()}`,
                getAuthHeader(),
            );
        },
        getNextPageParam: lastPage => {
            const page = Number(lastPage?.page || 1);
            const size = Number(lastPage?.page_size || normalizedPageSize);
            const total = Number(lastPage?.total || 0);
            return page * size < total ? page + 1 : undefined;
        },
        retry: false,
        staleTime: 60 * 1000,
    });
}

/**
 * Mailbox connections attached to a unified inbox (Gmail and Outlook).
 *
 * Each entry carries `provider` ('gmail' | 'outlook'), `email`, `connection_id`,
 * and `status` ('active' | 'pending' | 'reauth'). The MailboxSwitcher reads
 * this to show per-mailbox provider badges, and the compose/reply flow reads
 * it to make the mailbox selection explicit rather than relying on array order.
 */
export function useInboxMailboxes(inboxId) {
    const { getAuthHeader } = useAuth();
    return useQuery({
        queryKey: mailboxKeys.inboxRefs(inboxId),
        enabled: Boolean(inboxId),
        queryFn: () => apiFetch(`/inboxes/${inboxId}/mailboxes`, getAuthHeader()),
        retry: false,
    });
}

/**
 * Every mailbox this runtime holds credentials for, both providers, each row
 * carrying the inboxes it feeds.
 *
 * Replaces the pair of per-provider reads the admin panel used to do
 * (`useMailboxTokens` for Microsoft, `useInboxMailboxes(inboxes[0])` for Gmail).
 * The Gmail half was scoped to the FIRST inbox without saying so, so a mailbox
 * attached to any other inbox rendered as "none attached" while the grant was
 * live.
 */
export function useMailboxDirectory() {
    const { getAuthHeader } = useAuth();
    return useQuery({
        queryKey: ['mailbox-directory'],
        queryFn: () => apiFetch('/inboxes/mailboxes', getAuthHeader()),
        retry: false,
        staleTime: 15 * 1000,
    });
}

/**
 * Detach a mailbox from every inbox and destroy its stored credentials.
 *
 * Invalidates the inbox stats too: removing a mailbox changes the header counts
 * and the delivery-health banner, and leaving those cached shows an inbox still
 * claiming a mailbox it can no longer read.
 */
export function useRemoveMailbox() {
    const { getAuthHeader } = useAuth();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ provider, connectionId, mail }) =>
            apiPost(
                '/inboxes/mailboxes/remove',
                { provider, connectionId, mail },
                getAuthHeader()
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['mailbox-directory'] });
            queryClient.invalidateQueries({ queryKey: ['mailbox-tokens'] });
            queryClient.invalidateQueries({ queryKey: ['mailbox-access-requests'] });
            queryClient.invalidateQueries({ queryKey: inboxKeys.inboxes });
            queryClient.invalidateQueries({ queryKey: mailboxKeys.all });
            // Prefix-invalidate every per-inbox query at once — `['inbox', id,
            // …]` covers stats and mailbox refs. Removal changes the header
            // counts and the delivery-health banner, and a stale stats cache
            // leaves an inbox reporting a mailbox it can no longer read.
            queryClient.invalidateQueries({ queryKey: ['inbox'] });
        },
    });
}

/**
 * Attach a mailbox connection to a unified inbox.
 *
 * The connection must already exist (owner-consented OAuth). This mutation
 * associates it with the inbox so the inbox's reply/compose flows can use it.
 * The admin does NOT connect the mailbox here — the owner consents personally
 * (personal Settings) or via a consent request (admin wizard). This is the
 * "attach the resulting connection" step.
 */
export function useAttachMailbox(inboxId) {
    const { getAuthHeader } = useAuth();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ connectionId, ...body }) =>
            apiPost(`/inboxes/${inboxId}/mailboxes/${encodeURIComponent(connectionId)}/attach`, body, getAuthHeader()),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: mailboxKeys.inboxRefs(inboxId) });
            queryClient.invalidateQueries({ queryKey: inboxKeys.statsPrefix(inboxId) });
        },
    });
}

/**
 * Detach a mailbox connection from a unified inbox.
 *
 * Removes the association only — the underlying OAuth connection (owner's
 * personal token) is not revoked. The owner can re-attach later.
 */
export function useDetachMailbox(inboxId) {
    const { getAuthHeader } = useAuth();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: connectionId =>
            apiPost(
                `/inboxes/${inboxId}/mailboxes/${encodeURIComponent(connectionId)}/detach`,
                {},
                getAuthHeader()
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: mailboxKeys.inboxRefs(inboxId) });
            queryClient.invalidateQueries({ queryKey: inboxKeys.statsPrefix(inboxId) });
        },
    });
}


/**
 * The same people picker, for a MANAGER of one inbox.
 *
 * `/admin/inbox/employees` is admin-gated, so the per-inbox settings surface
 * derived its list from the inbox's members instead — which made the two
 * configuration modals disagree about who could be granted access, and hid
 * any grant held by a non-member. `GET /inboxes/{id}/employees` is the
 * manager-legal equivalent and returns the identical shape.
 */
export function useInboxEmployeesForInbox(inboxId, enabled = false) {
    const { getAuthHeader } = useAuth();
    return useQuery({
        queryKey: [...inboxKeys.employees, inboxId],
        enabled: Boolean(inboxId) && enabled,
        queryFn: () => apiFetch(`/inboxes/${inboxId}/employees`, getAuthHeader()),
        select: data => data?.data || [],
        retry: false,
        staleTime: 10 * 60 * 1000,
    });
}

export function useInboxEmployees(enabled = false) {
    const { getAuthHeader } = useAuth();
    return useQuery({
        queryKey: inboxKeys.employees,
        enabled,
        queryFn: () => apiFetch('/admin/inbox/employees', getAuthHeader()),
        select: data => data?.data || [],
        retry: false,
        staleTime: 10 * 60 * 1000,
    });
}

// ─── Inbox CRUD ──────────────────────────────────────────────────────────────

export function useCreateInbox() {
    const { getAuthHeader } = useAuth();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: body => apiPost('/inboxes', body, getAuthHeader()),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: inboxKeys.inboxes });
        },
    });
}

export function useUpdateInbox() {
    const { getAuthHeader } = useAuth();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ inboxId, ...body }) =>
            apiPatch(`/inboxes/${inboxId}`, body, getAuthHeader()),
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: inboxKeys.inboxes });
            queryClient.invalidateQueries({ queryKey: inboxKeys.statsPrefix(variables.inboxId) });
            // #803 — removing a member's mailbox moved its mail into this
            // inbox's Trash, and the server reconciled the bell (H8).
            if (data?.removed_messages) notifyBellChanged();
        },
    });
}

export function useRecoverOrphanedInbox() {
    const { getAuthHeader } = useAuth();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: inboxId =>
            apiPost(`/inboxes/${encodeURIComponent(inboxId)}/recover`, {}, getAuthHeader()),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: inboxKeys.inboxes });
            queryClient.invalidateQueries({ queryKey: inboxKeys.orphans });
        },
    });
}


export function useDeleteInbox() {
    const { getAuthHeader } = useAuth();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: inboxId => apiDelete(`/inboxes/${inboxId}`, getAuthHeader()),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: inboxKeys.inboxes });
            notifyBellChanged(); // #803 H10 — entries only this inbox could open are gone
        },
    });
}

/**
 * Remove one message from an inbox's view. CRM-side only — the member's real
 * mailbox is never touched (Outlook-side deletions propagate in on their own
 * via the `deleted` change notifications).
 */
export function useDeleteInboxMessage(inboxId) {
    const { getAuthHeader } = useAuth();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: commId =>
            apiDelete(
                `/inboxes/${inboxId}/messages/${encodeURIComponent(commId)}`,
                getAuthHeader()
            ),
        onSuccess: (_data, commId) => {
            // Filters are part of the messages key — invalidate every variant,
            // and the stats the header counts read.
            //
            // Except the removed conversation's own thread: this removes the
            // whole conversation from the inbox, so refetching it can only
            // answer 404. The reader deletes by the same seed its thread is keyed
            // on and leaves straight after, so an active refetch here was one
            // wasted request and a console error per Delete. It is still marked
            // stale, never refetched.
            const isRemovedThread = query =>
                query.queryKey[2] === 'thread' && query.queryKey[3] === commId;
            queryClient.invalidateQueries({
                predicate: query =>
                    query.queryKey[0] === 'inbox' &&
                    query.queryKey[1] === inboxId &&
                    (query.queryKey[2] === 'messages' || query.queryKey[2] === 'thread') &&
                    !isRemovedThread(query),
            });
            queryClient.invalidateQueries({
                predicate: query =>
                    query.queryKey[0] === 'inbox' &&
                    query.queryKey[1] === inboxId &&
                    isRemovedThread(query),
                refetchType: 'none',
            });
            queryClient.invalidateQueries({ queryKey: inboxKeys.statsPrefix(inboxId) });
            // The rail reads its own lane key (#710) — deletion changes counts.
            queryClient.invalidateQueries({ queryKey: mailFolderKeys.lane(inboxId) });
            // #803 — the server moved every member's entry to "In Trash".
            notifyBellChanged();
        },
    });
}

export function useBulkDeleteInboxMessages(inboxId) {
    const { getAuthHeader } = useAuth();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: commIds =>
            apiPost(
                `/inboxes/${inboxId}/messages/bulk-delete`,
                { comm_ids: commIds },
                getAuthHeader()
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({
                predicate: query =>
                    query.queryKey[0] === 'inbox' &&
                    query.queryKey[1] === inboxId &&
                    (query.queryKey[2] === 'messages' || query.queryKey[2] === 'thread'),
            });
            queryClient.invalidateQueries({ queryKey: inboxKeys.statsPrefix(inboxId) });
            // The rail reads its own lane key (#710) — deletion changes counts.
            queryClient.invalidateQueries({ queryKey: mailFolderKeys.lane(inboxId) });
            notifyBellChanged(); // #803
        },
    });
}

/**
 * The Trash verbs on this lane: restore, purge and empty, plus the reader's
 * per-message move into Trash (`useDeleteOneInboxMessage`).
 *
 * Trash on a pooled inbox is the per-inbox REMOVAL RECORD, not the personal
 * lane's `mail_folder: "trash"` — that field belongs to the row, and a
 * message legitimately sits in several inboxes, so one department's bin
 * would empty another department's list. Delete writes the record and
 * therefore already moves a message to Trash; restore, purge and empty are
 * what the bin needs to be a bin.
 *
 * `useDeleteInboxMessage` above is unchanged and is the move. Nothing here
 * tombstones a row: "permanently" on this lane means permanently FOR THIS
 * INBOX, which is the only thing a pooled view has standing to decide.
 */
function useTrashMutation(inboxId, request, { emptiesTrashThread = false, permanent = false } = {}) {
    const { getAuthHeader } = useAuth();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: arg => request(arg, getAuthHeader()),
        onSuccess: (_data, arg) => {
            // Same shape as the delete these undo: filters are part of the
            // messages key, so every variant is invalidated, and the rail's
            // counts move with them.
            //
            // Restore and purge take the conversation OUT of Trash, so the
            // reader's Trash view of that seed (`useInboxTrashThread`) can only
            // 404 if refetched — and the reader leaves straight after. Marked
            // stale, never refetched; the same reasoning as
            // `useDeleteInboxMessage`.
            const goneKey = emptiesTrashThread
                ? JSON.stringify([...inboxKeys.thread(inboxId, arg), 'trash'])
                : null;
            const isGone = query => goneKey !== null && JSON.stringify(query.queryKey) === goneKey;
            // #803 — Delete forever / Empty Trash: the threads they killed can
            // only 404 on a refetch, so their caches go. Delete forever names
            // its conversation, so only threads showing it go — never the Trash
            // view a reader may still be observing (`isGone`, marked stale
            // below). Empty Trash empties a bin the client cannot enumerate, and
            // runs from the list, so every thread of the inbox goes.
            if (permanent && arg) {
                removeThreadsContaining(queryClient, ['inbox', inboxId, 'thread'], [arg], { keep: isGone });
            } else if (permanent) {
                queryClient.removeQueries({ queryKey: ['inbox', inboxId, 'thread'] });
            }
            queryClient.invalidateQueries({
                predicate: query =>
                    query.queryKey[0] === 'inbox' &&
                    query.queryKey[1] === inboxId &&
                    (query.queryKey[2] === 'messages' || query.queryKey[2] === 'thread') &&
                    !isGone(query),
            });
            if (goneKey) {
                queryClient.invalidateQueries({ predicate: isGone, refetchType: 'none' });
            }
            queryClient.invalidateQueries({ queryKey: inboxKeys.statsPrefix(inboxId) });
            // The rail reads its own lane key (#710) — restore and purge move
            // counts exactly the way the two deletes above do. `['mail',
            // 'folders']` stays too: a legacy rail still mounted on the old
            // bare key must not keep a stale count while it lives.
            queryClient.invalidateQueries({ queryKey: mailFolderKeys.lane(inboxId) });
            queryClient.invalidateQueries({ queryKey: ['mail', 'folders'] });
            // #803 — the server already reconciled the bell (a restore just
            // drops the "In Trash" label, computed when the bell is read).
            notifyBellChanged();
        },
    });
}

/**
 * Move ONE message of a conversation to this inbox's Trash — the reader's
 * per-message ⋯ → Delete. `useDeleteInboxMessage` removes the whole
 * conversation, which is the list row's verb; this is the same server write
 * (`extent=message`) over that message's per-mailbox copies only, so it lands
 * in the same Trash and restores through `useRestoreInboxMessage`.
 *
 * Never `POST /mail/move {folder: 'trash'}` on this lane: that flags the
 * shared ROW, so the message left every inbox it was tagged into and reached
 * no Trash. The server now refuses it.
 */
export function useDeleteOneInboxMessage(inboxId) {
    return useTrashMutation(inboxId, (commId, headers) =>
        apiDelete(
            `/inboxes/${inboxId}/messages/${encodeURIComponent(commId)}?extent=message`,
            headers,
        ));
}

export function useRestoreInboxMessage(inboxId) {
    return useTrashMutation(inboxId, (commId, headers) =>
        apiPost(
            `/inboxes/${inboxId}/trash/${encodeURIComponent(commId)}/restore`,
            {},
            headers,
        ), { emptiesTrashThread: true });
}

export function usePurgeInboxMessage(inboxId) {
    return useTrashMutation(inboxId, (commId, headers) =>
        apiDelete(
            `/inboxes/${inboxId}/trash/${encodeURIComponent(commId)}`,
            headers,
        ), { emptiesTrashThread: true, permanent: true });
}

export function useEmptyInboxTrash(inboxId) {
    return useTrashMutation(inboxId, (_arg, headers) =>
        apiPost(`/inboxes/${inboxId}/trash/empty`, {}, headers), { permanent: true });
}

/**
 * The addresses the SERVER says this caller may send as.
 *
 * `inboxRules.fromOptions` builds the Reply-as list from
 * `inbox.member_smtps`, which is a DECLARED pool — an admin types an address
 * and it is in the list whether or not its owner has consented, and until
 * #703 it stayed there after revocation too. The send path resolves a
 * different, narrower set, so the picker offered identities whose send was
 * guaranteed to fail (#700).
 *
 * `undefined` while loading or on error, deliberately: the caller treats
 * "no answer" as "do not filter". A failed request must not look like an
 * authoritative empty list and lock the user out of composing.
 */
export function useInboxSenders(inboxId) {
    const { getAuthHeader } = useAuth();
    const query = useQuery({
        queryKey: ['inbox', inboxId, 'senders'],
        queryFn: () =>
            apiFetch(`/inboxes/${inboxId}/senders`, getAuthHeader()).then(r => r.data),
        enabled: Boolean(inboxId),
        staleTime: 60_000,
        retry: 1,
    });
    return {
        sendableAddresses: query.isSuccess ? (query.data?.allowed ?? []) : undefined,
        defaultFrom: query.data?.default_from ?? null,
        isLoading: query.isLoading,
    };
}

// ─── Sends ───────────────────────────────────────────────────────────────────

/**
 * Reply and compose invalidate the message list and thread, but do NOT optimistically
 * flip `replied`.
 *
 * The flip arrives through the SentItems ingest pipeline: the send lands in the
 * sending mailbox's Sent Items, Graph notifies, and reply detection attributes it.
 * Faking it here would show a reply as detected before it was, and would show the
 * wrong thing entirely if the send failed downstream of Graph accepting it.
 */
export function useReplyToMessage(inboxId) {
    const { getAuthHeader } = useAuth();
    const queryClient = useQueryClient();
    return useMutation({
        // #970 audit F09 — no signature marker or id in sent mail.
        mutationFn: ({ messageId, conversationId, ...body }) =>
            apiPost(
                `/inboxes/${inboxId}/messages/${encodeURIComponent(messageId)}/reply`,
                { ...body, body: withoutSignatureBlocks(body.body) },
                getAuthHeader()
            ),
        onSuccess: (_data, variables) => {
            // Start a 30-second post-send polling window for THIS thread only
            // so useInboxThread refetches every 3s, catching the SentItems
            // webhook ingest. Per-thread scoping prevents polling on other
            // threads that were never replied to.
            if (variables.conversationId) {
                _postSendPollingUntil.set(
                    `${inboxId}:${variables.conversationId}`,
                    Date.now() + 30000
                );
            }
            queryClient.invalidateQueries({ queryKey: ['inbox', inboxId, 'messages'] });
            queryClient.invalidateQueries({ queryKey: ['inbox', inboxId, 'thread'] });
            queryClient.invalidateQueries({ queryKey: inboxKeys.statsPrefix(inboxId) });
        },
    });
}

export function useComposeFromInbox(inboxId) {
    const { getAuthHeader } = useAuth();
    const queryClient = useQueryClient();
    return useMutation({
        // #970 audit F09 — no signature marker or id in sent mail.
        mutationFn: body => apiPost(
            `/inboxes/${inboxId}/compose`,
            { ...body, body: withoutSignatureBlocks(body.body) },
            getAuthHeader(),
        ),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['inbox', inboxId, 'messages'] });
            queryClient.invalidateQueries({ queryKey: inboxKeys.statsPrefix(inboxId) });
        },
    });
}

// ─── Setup wizard ────────────────────────────────────────────────────────────

export function useVerifyMicrosoft() {
    const { getAuthHeader } = useAuth();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => apiPost('/oauth/microsoft/verify', {}, getAuthHeader()),
        onSuccess: () => {
            // Verify reconciles subscriptions before reporting (it is a repair
            // control, not a read-out), and the 0/N banner reads per-inbox
            // stats. Without this a successful repair keeps showing the stale
            // red banner until some unrelated refetch. Verify is fleet-wide, so
            // every inbox's stats key is invalidated by predicate.
            queryClient.invalidateQueries({
                predicate: query =>
                    query.queryKey[0] === 'inbox' && query.queryKey[2] === 'stats',
            });
        },
    });
}

// ─── Per-user delegated mailbox access ────────────────────────────────────────

/**
 * All mailbox delegated-access tokens with their status (active, pending,
 * expired, revoked). Admin-only. Drives the inbox editor's per-mailbox status
 * badges and the setup wizard's stat cards.
 */
export function useMailboxTokens() {
    const { getAuthHeader, user } = useAuth();
    return useQuery({
        queryKey: ['mailbox-tokens'],
        enabled: isOrgAdminUser(user),
        queryFn: () => apiFetchArray('/oauth/microsoft/mailbox-tokens', getAuthHeader()),
        retry: false,
        staleTime: 30 * 1000,
    });
}

/**
 * All pending and historical mailbox access requests. Admin-only. Drives the
 * inbox editor's per-mailbox "Request Sent" status badge, which reflects a
 * request that has been emailed to the mailbox owner but not yet consented to.
 */

export function useMailboxAccessRequests(provider = 'microsoft', inboxId = null) {
    const { getAuthHeader, user } = useAuth();
    const scopedInboxId = inboxId || null;
    return useQuery({
        queryKey: ['mailbox-access-requests', provider, scopedInboxId || 'all'],
        enabled: Boolean(scopedInboxId) || isOrgAdminUser(user),
        queryFn: () => {
            const params = new URLSearchParams({ provider });
            if (scopedInboxId) params.set('inboxId', scopedInboxId);
            return apiFetchArray(
                `/oauth/microsoft/mailbox-access/requests?${params.toString()}`,
                getAuthHeader(),
            );
        },
        retry: false,
        staleTime: 30 * 1000,
    });
}


/**
 * Refetch every store that answers "what is this mailbox's status?".
 *
 * There are four, and a consent mutation changes the answer in all of them:
 *
 * - `mailbox-tokens` — Microsoft delegated grants.
 * - `mailbox-access-requests` — the control plane's request rows.
 * - `mailbox-directory` — the Active / Pending / Needs Reauth / Revoked tiles.
 * - `['inbox', <id>, 'mailboxes']` — the attached mailbox refs, which is where
 *   a GOOGLE grant's status lives. `MailboxEmailInput.tokenStatus` reads it via
 *   `connectionStatusFor`, because `tokens` only ever holds Microsoft rows.
 *
 * Revoke, resend and cancel invalidated only the first two. For a Microsoft
 * member that happens to be enough and the row updated instantly; for a Gmail
 * member the store that decides its badge was never refetched, so the row kept
 * rendering "Approved" with a Revoke button after a successful revoke and only
 * a manual page reload corrected it.
 *
 * The refs are matched by PREDICATE rather than by `inboxRefs(inboxId)`. These
 * mutations are keyed on an address, not an inbox, and one mailbox can be
 * pooled into several inboxes — revoking its grant changes its status in every
 * one of them, so every one has to be refetched.
 */
function invalidateMailboxStatus(queryClient) {
    queryClient.invalidateQueries({ queryKey: ['mailbox-tokens'] });
    queryClient.invalidateQueries({ queryKey: ['mailbox-access-requests'] });
    queryClient.invalidateQueries({ queryKey: ['mailbox-directory'] });
    queryClient.invalidateQueries({
        predicate: query =>
            Array.isArray(query.queryKey) &&
            query.queryKey[0] === 'inbox' &&
            query.queryKey[2] === 'mailboxes',
    });
}

/**
 * Request delegated access to a Microsoft mailbox. The runtime asks the control
 * plane to create an access request and email the mailbox owner a consent link.
 */
export function useRequestMailboxAccess() {
    const { getAuthHeader } = useAuth();
    const queryClient = useQueryClient();
    return useMutation({
        // `provider` is OMITTED when the caller does not know it, never coerced
        // to 'microsoft'. That `|| 'microsoft'` was the guess `detectProvider`
        // was deleted for, reinstated one layer below the call sites that had
        // been fixed to omit the field: the runtime then read an EXPLICIT
        // provider assertion and rejected every gmail.com address as
        // `invalid_email`, because asserting microsoft for a gmail.com mailbox
        // is exactly what its validator exists to catch. The control plane
        // resolves the platform from MX; sending nothing is the correct way to
        // say "you decide".
        mutationFn: ({ mailboxEmail, inboxId, provider }) =>
            apiPost(
                '/oauth/microsoft/mailbox-access/request',
                { mailboxEmail, inboxId, ...(provider ? { provider } : {}) },
                getAuthHeader()
            ),
        onSuccess: () => invalidateMailboxStatus(queryClient),
    });
}

/**
 * Revoke delegated access for a specific mailbox — tears down subscriptions and
 * marks the token revoked.
 */
export function useRevokeMailboxAccess() {
    const { getAuthHeader } = useAuth();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: mail =>
            apiDelete(
                `/oauth/microsoft/mailbox-tokens/${encodeURIComponent(mail)}`,
                getAuthHeader()
            ),
        onSuccess: () => {
            invalidateMailboxStatus(queryClient);
            notifyBellChanged(); // #803 H7 — the revoke purged that mailbox's shared mail
        },
    });
}

/**
 * Resend the access-request email for a pending or expired mailbox.
 */
export function useResendMailboxAccess() {
    const { getAuthHeader } = useAuth();
    const queryClient = useQueryClient();
    return useMutation({
        // The query param is dropped entirely when the provider is unknown —
        // same reason as `useRequestMailboxAccess`. An absent `?provider=` means
        // "resolve it", whereas `?provider=microsoft` on a gmail.com address is
        // a claim the runtime is right to refuse.
        mutationFn: ({ mail, provider }) =>
            apiPost(
                `/oauth/microsoft/mailbox-tokens/${encodeURIComponent(mail)}/resend${
                    provider ? `?provider=${encodeURIComponent(provider)}` : ''
                }`,
                {},
                getAuthHeader()
            ),
        onSuccess: () => invalidateMailboxStatus(queryClient),
    });
}

/**
 * Cancel a pending/sent mailbox access request. The admin uses this when
 * removing a mailbox that has a pending request but no approved token yet.
 * The control plane marks the MailboxAccessRequest as 'revoked' so the partial
 * unique index releases, allowing a fresh request to be created later.
 */
export function useCancelMailboxAccess() {
    const { getAuthHeader } = useAuth();
    const queryClient = useQueryClient();
    return useMutation({
        // Omitted when unknown, as above. The control plane's cancel lookup is
        // provider-scoped, so a wrong segment answers `request_not_found`
        // rather than cancelling the request the admin is looking at.
        mutationFn: ({ mail, provider }) =>
            apiPost(
                '/oauth/microsoft/mailbox-access/cancel',
                { mailboxEmail: mail, ...(provider ? { provider } : {}) },
                getAuthHeader()
            ),
        onSuccess: () => invalidateMailboxStatus(queryClient),
    });
}

// ─── Selection persistence ───────────────────────────────────────────────────

/**
 * The last mailbox this user selected, remembered across sessions.
 *
 * Falls back to My Inbox when the stored id is no longer in the caller's list —
 * a grant can be revoked between visits, and restoring a selection the server
 * would now 403 puts the user on an error screen for something they did last week.
 */
export function useLastMailbox(storageKey) {
    const [lastMailboxId, setLastMailboxIdState] = useState(() => {
        try {
            return window.localStorage.getItem(storageKey);
        } catch {
            // Private-browsing modes and blocked storage throw on access rather
            // than returning null. Losing the preference is acceptable; crashing
            // the inbox over it is not.
            return null;
        }
    });

    const setLastMailboxId = useCallback(
        value => {
            setLastMailboxIdState(value);
            try {
                if (value) window.localStorage.setItem(storageKey, value);
                else window.localStorage.removeItem(storageKey);
            } catch {
                /* see useState initializer */
            }
        },
        [storageKey]
    );

    return { lastMailboxId, setLastMailboxId };
}
